import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

export default function QuoteApprovalPage() {
  const [params] = useSearchParams();
  const token     = params.get('token');
  const urlAction = params.get('action'); // 'approve' | 'revise'

  const [state,      setState]      = useState('loading');
  const [tokenDoc,   setTokenDoc]   = useState(null);
  const [action,     setAction]     = useState(null);   // 'approve' | 'revise'
  const [reviseMode, setReviseMode] = useState(null);   // 'edit' | 'list'
  const [comment,    setComment]    = useState('');
  const [errorMsg,   setErrorMsg]   = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('No approval token found in this link.'); return; }
    (async () => {
      const tokenSnap = await getDoc(doc(db, 'quoteApprovalTokens', token));
      if (!tokenSnap.exists()) { setState('error'); setErrorMsg('This link is invalid or has expired.'); return; }
      const td = tokenSnap.data();
      if (td.used) { setState('already_used'); setTokenDoc(td); return; }
      // Display fields are denormalized onto the token doc itself — Michael
      // may not be logged in when clicking through from the email, and this
      // page must never need to read the parent request doc directly.
      setTokenDoc(td);
      if (urlAction === 'approve' || urlAction === 'revise') setAction(urlAction);
      setState('ready');
    })().catch((err) => { console.error(err); setState('error'); setErrorMsg('Failed to load. Please try again.'); });
  }, [token, urlAction]);

  async function handleSubmit() {
    let finalAction;
    if (action === 'approve') finalAction = 'approve';
    else if (reviseMode === 'edit') finalAction = 'revise_edit';
    else if (reviseMode === 'list' && comment.trim()) finalAction = 'revise_list';
    else return;

    setState('submitting');
    try {
      await updateDoc(doc(db, 'quoteApprovalTokens', token), {
        action: finalAction, comment: finalAction === 'revise_list' ? comment.trim() : null,
        used: true, respondedAt: serverTimestamp(),
      });
      setAction(finalAction);
      setState('done');
    } catch (err) {
      console.error(err);
      setState('error');
      setErrorMsg('Failed to submit your response. Please try again.');
    }
  }

  const tourName = tokenDoc?.tourName || '';
  const sym = CURRENCY_SYMBOLS[tokenDoc?.currency] || '£';
  const costPerPerson = tokenDoc?.costPerPerson;

  if (state === 'loading') return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
        <p className="text-[15px] font-semibold text-[#111827] mb-2">Link not valid</p>
        <p className="text-[13px] text-[#6B7280]">{errorMsg}</p>
      </div>
    </div>
  );

  if (state === 'already_used') {
    const label = tokenDoc?.action === 'approve' ? 'Approved'
      : tokenDoc?.action === 'revise_list' ? 'Revision requested'
      : tokenDoc?.action === 'revise_edit' ? "You chose to edit it yourself"
      : 'Responded';
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <p className="text-[15px] font-semibold text-[#111827] mb-2">Response already recorded</p>
          <p className="text-[13px] text-[#6B7280]">You have already submitted: <strong>{label}</strong>.</p>
          {tokenDoc?.comment && <p className="text-[12px] text-[#6B7280] mt-2 italic">"{tokenDoc.comment}"</p>}
        </div>
      </div>
    );
  }

  if (state === 'done') {
    const messages = {
      approve:      { title: 'Quote approved', body: 'The quote has been sent to the customer.' },
      revise_list:  { title: 'Notes sent', body: 'Your notes have been emailed to the team to action.' },
      revise_edit:  { title: 'Go ahead and edit it', body: 'No email was sent — head into the portal to make your changes directly.' },
    };
    const msg = messages[action] || messages.approve;
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">{msg.title}</p>
          <p className="text-[13px] text-[#6B7280]">{msg.body}</p>
          {action === 'revise_edit' && (
            <a href={`https://msttoursportal.co.uk/staff/requests/${tokenDoc?.requestId}#quote`}
              className="inline-block mt-4 px-5 py-2.5 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[13px] rounded-[8px] transition">
              Open quote in portal
            </a>
          )}
        </div>
      </div>
    );
  }

  const canSubmit = action === 'approve'
    || (action === 'revise' && reviseMode === 'edit')
    || (action === 'revise' && reviseMode === 'list' && comment.trim());

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="bg-[#0D2240] rounded-[12px] p-6 text-center">
          <p className="text-white font-bold text-[18px]">MST Sports Tours — Quote Review</p>
          <p className="text-[#93C5FD] text-[13px] mt-1">Internal Approval</p>
        </div>

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <p className="text-[15px] font-bold text-[#111827] mb-1">{tourName}</p>
          <p className="text-[13px] text-[#6B7280]">
            {[formatDate(tokenDoc?.departureDate), formatDate(tokenDoc?.returnDate)].filter(Boolean).join(' – ')}
            {tokenDoc?.participants ? ` · ${tokenDoc.participants} participants` : ''}
          </p>
          {costPerPerson != null && (
            <p className="text-[13px] text-[#374151] mt-2">
              Cost per person: <strong>{sym}{Number(costPerPerson).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</strong>
            </p>
          )}
          <p className="text-[12px] text-[#9CA3AF] mt-2">
            Please review the attached quote PDF, then approve it or send it back for changes.
          </p>
        </div>

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <p className="text-[13px] font-semibold text-[#374151] mb-4">Your decision</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { id: 'approve', label: '✓ Approve', cls: action === 'approve' ? 'border-[#16A34A] bg-[#F0FDF4] text-[#15803D]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#16A34A]' },
              { id: 'revise',  label: '✏ Revise',   cls: action === 'revise'  ? 'border-[#D97706] bg-[#FFFBEB] text-[#D97706]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#D97706]' },
            ].map(({ id, label, cls }) => (
              <button key={id} type="button" onClick={() => { setAction(id); setReviseMode(null); setComment(''); }}
                className={`border-2 rounded-[10px] px-3 py-3 text-[13px] font-semibold transition text-center ${cls}`}>
                {label}
              </button>
            ))}
          </div>

          {action === 'revise' && (
            <div className="mb-4">
              <p className="text-[12.5px] font-medium text-[#374151] mb-2">How would you like to handle it?</p>
              <div className="grid grid-cols-1 gap-2 mb-3">
                {[
                  { id: 'edit', label: "I'll edit it myself" },
                  { id: 'list', label: 'List what needs amending' },
                ].map(({ id, label }) => (
                  <button key={id} type="button" onClick={() => setReviseMode(id)}
                    className={`border-2 rounded-[10px] px-3 py-2.5 text-[13px] font-medium text-left transition ${
                      reviseMode === id ? 'border-pb bg-[#F0F7FF] text-pb' : 'border-[#E5E7EB] text-[#374151] hover:border-pb'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {reviseMode === 'list' && (
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">
                    What needs amending? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    placeholder="e.g. The per-person price looks too low for this hotel — please re-check the cost sheet…"
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] resize-none"
                  />
                  <p className="text-[11px] text-[#9CA3AF] mt-1">This will be emailed to the team so they can action it.</p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit || state === 'submitting'}
            className="w-full py-3 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[14px] rounded-[10px] transition disabled:opacity-50"
          >
            {state === 'submitting' ? 'Submitting…' : 'Submit decision'}
          </button>
        </div>
      </div>
    </div>
  );
}
